import { useApolloClient } from '@vue/apollo-composable';
import { downloadPdf } from '@/utils/downloadPdf';
import type { Bill, BillDuplicateWarning, CheckBillDuplicatesInput } from '@/types/composition-functions';
import {
  useGetBillsLazyQuery,
  useGetBillLazyQuery,
  useCreateBillsMutation,
  useUpdateBillMutation,
  usePublishBillMutation,
  useVoidBillMutation,
  BillInvoicePdfDocument,
  CheckBillDuplicatesDocument,
} from '@/types/vue-apollo-helpers';
import { evictBillingAndCatalogueQueries, evictBillingQueries } from '@/utils/billingCache';

// Re-export for consumers
export type { Bill };

interface BillsFilter {
  billable_type?: string | null;
  billable_id?: string | null;
  classroom_id?: string | null;
  session_id?: string | null;
  academic_calendar_id?: string | null;
  status?: string[] | null;
  search?: string | null;
}

/**
 * Bills list. `initialStatus` is the status filter the list starts with
 * (lowercase DB values); callers that want every live bill pass all of them.
 */
export const useBills = (options: { autoFetch?: boolean; initialStatus?: string[] } = {}) => {
  const filters = reactive<BillsFilter>({
    status: options.initialStatus ? [...options.initialStatus] : ['published'],
  });

  const {
    result,
    loading,
    error,
    refetch,
    load,
  } = useGetBillsLazyQuery(
    () => ({
      billable_type: filters.billable_type || undefined,
      billable_id: filters.billable_id || undefined,
      classroom_id: filters.classroom_id || undefined,
      session_id: filters.session_id || undefined,
      academic_calendar_id: filters.academic_calendar_id || undefined,
      status: filters.status?.length ? filters.status : undefined,
      search: filters.search || undefined,
    }),
    // Revisits show the cached list at once, then pick up payments made
    // elsewhere (webhooks, other staff) without a page reload.
    { fetchPolicy: 'cache-and-network' },
  );
  if (options.autoFetch) load();

  const bills = computed(() => result.value?.bills ?? []);

  return {
    bills,
    filters,
    loading,
    error,
    refetch,
    load,
  };
};

export const useBill = (id: string) => {
  const {
    result,
    loading,
    error,
    refetch,
    load,
  } = useGetBillLazyQuery({ id }, { fetchPolicy: 'cache-and-network' });
  load();

  const bill = computed(() => result.value?.bill ?? null);

  return { bill, loading, error, refetch, load };
};

/**
 * "Download bill (PDF)". Queried on demand rather than through a watched
 * lazy query, so every click downloads (even when the PDF is unchanged) and
 * a failure reaches the caller instead of leaving the button spinning.
 */
export const useBillPdfDownload = () => {
  const { resolveClient } = useApolloClient();
  const downloading = ref(false);

  const download = async(billId: string): Promise<void> => {
    downloading.value = true;
    try {
      const { data } = await resolveClient().query({
        query: BillInvoicePdfDocument,
        variables: { bill_id: billId },
        fetchPolicy: 'network-only',
      });
      const pdf = data?.billInvoicePdf;
      if (!pdf?.base64) throw new Error('The bill PDF came back empty.');
      downloadPdf(pdf.base64, pdf.filename);
    } finally {
      downloading.value = false;
    }
  };

  return { download, downloading };
};

export const useCreateBills = () => {
  const {
    mutate: createBills,
    loading,
    onDone,
    onError,
  } = useCreateBillsMutation({
    // "Save as reusable template" can add fee items too.
    update: evictBillingAndCatalogueQueries,
  });

  return {
    createBills: (input: {
      billable_type: string;
      billable_ids: string[];
      session_id?: string;
      academic_calendar_id?: string;
      classroom_id?: string;
      due_date?: string;
      status?: string;
      items: Array<{
        bill_item_template_id?: string;
        item_name?: string;
        item_description?: string;
        bill_item_category_id?: string;
        amount?: number;
      }>;
      acknowledge_duplicates?: boolean;
    }) => createBills({ input }),
    loading,
    onDone,
    onError,
  };
};

export const useUpdateBill = () => {
  const {
    mutate: updateBill,
    loading,
    onDone,
    onError,
  } = useUpdateBillMutation({
    update: evictBillingQueries,
  });

  return {
    updateBill: (input: {
      id: string;
      items: Array<{
        id?: string;
        bill_item_template_id?: string;
        item_name?: string;
        item_description?: string;
        bill_item_category_id?: string;
        amount?: number;
      }>;
      due_date?: string;
      acknowledge_duplicates?: boolean;
    }) => updateBill({ input }),
    loading,
    onDone,
    onError,
  };
};

export const usePublishBill = () => {
  const { mutate, loading, onDone, onError } = usePublishBillMutation({
    update: evictBillingQueries,
  });
  return {
    publishBill: (id: string) => mutate({ id }),
    loading,
    onDone,
    onError,
  };
};

export const useVoidBill = () => {
  const { mutate, loading, onDone, onError } = useVoidBillMutation({
    update: evictBillingQueries,
  });
  return {
    voidBill: (id: string) => mutate({ id }),
    loading,
    onDone,
    onError,
  };
};

/**
 * Preflight duplicate check — returns likely-duplicate warnings for the proposed
 * items without creating anything, so the UI can advise before submitting.
 */
export const useCheckBillDuplicates = () => {
  const { resolveClient } = useApolloClient();

  const check = async(input: CheckBillDuplicatesInput): Promise<BillDuplicateWarning[]> => {
    const { data } = await resolveClient().query({
      query: CheckBillDuplicatesDocument,
      variables: { input },
      fetchPolicy: 'network-only',
    });

    return data?.checkBillDuplicates ?? [];
  };

  return { check };
};
